/**
 * The beer sip: state, roll and envelope.
 *
 * ------------------------------------------------------------------------
 *  DETERMINISM CONTRACT
 * ------------------------------------------------------------------------
 *  `rollSip(step, sipIndex)` is a pure function of its two arguments. It holds
 *  no state, reads no clock and calls no Math.random(). The PRNG it uses is
 *  the seeded one from src/utils/prng.ts, given a seed derived from the step
 *  the press landed on. The side alternates by sip index, starting left; only
 *  the angle, strength and duration vary with timing. The same replay produces
 *  the same sips on the server as it did in the browser.
 *
 *  Everything else here is plain bookkeeping over `BeerState`, which lives
 *  inside `PlayerState` and is therefore reset with the run like the rest of
 *  the simulation. See the header of src/config/beer.ts for why the mechanic
 *  is shaped this way.
 * ------------------------------------------------------------------------
 */

import { BEER } from '../config/beer';
import { normalize2, vec2, type Vec2 } from '../utils/math';
import { mulberry32, randRange } from '../utils/prng';

/** One sip's worth of consequences, decided the moment E is pressed. */
export interface SipRoll {
  /** Unit direction of the lurch in local space (x = right, y = forward). */
  direction: Vec2;
  /** Peak strength, in wish-direction units. */
  strength: number;
  /** How long it takes to swell and fade, in seconds. */
  duration: number;
}

/** The bottle, as far as the simulation is concerned. */
export interface BeerState {
  /** Extra vauhti multiplier from recent sips. Decays every step. */
  boost: number;
  /** Seconds until E buys another sip. */
  cooldown: number;
  /** Sips taken this run. Drives side alternation and the live alcohol level. */
  sips: number;
  /** The lurch currently in flight, and how much of it is left. */
  direction: Vec2;
  strength: number;
  timer: number;
  duration: number;
}

/** What the movement code needs from the sip in flight, this step. */
export interface BeerSample {
  /** Added to the requested movement direction. */
  wish: Vec2;
  /** Applied straight to velocity, m/s^2, in local space. */
  accel: Vec2;
  /** 0..1, how hard the lurch is hitting right now. */
  intensity: number;
}

export function createBeerState(): BeerState {
  return {
    boost: 0,
    cooldown: 0,
    sips: 0,
    direction: vec2(),
    strength: 0,
    timer: 0,
    duration: 0,
  };
}

export function resetBeerState(state: BeerState): void {
  state.boost = 0;
  state.cooldown = 0;
  state.sips = 0;
  state.direction.x = 0;
  state.direction.y = 0;
  state.strength = 0;
  state.timer = 0;
  state.duration = 0;
}

/**
 * Scrambles the two inputs into a seed.
 *
 * Timing varies the angle, strength and duration independently between steps.
 * It does not choose the side: that follows the number of successful sips.
 */
function sipSeed(step: number, sipIndex: number): number {
  let h = Math.imul(step | 0, 0x9e3779b1) ^ Math.imul((sipIndex | 0) + 1, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h ^ BEER.seed) >>> 0;
}

/**
 * The dice. Pure: same step and same sip index, same lurch, forever.
 *
 * Rounded to three decimals for the same reason the stumble schedule is - it
 * costs nothing, and it makes the F3 overlay and a failing test readable.
 */
export function rollSip(step: number, sipIndex: number): SipRoll {
  const rng = mulberry32(sipSeed(step, sipIndex));
  const side = sipIndex % 2 === 0 ? -1 : 1;
  const lateral = side * randRange(rng, 0.7, 1);
  const forward = randRange(rng, -1, 1) * BEER.forwardBias;
  const direction = normalize2({ x: lateral, y: forward });
  return {
    direction: {
      x: Math.round(direction.x * 1000) / 1000,
      y: Math.round(direction.y * 1000) / 1000,
    },
    strength: Math.round(randRange(rng, BEER.minStrength, BEER.maxStrength) * 1000) / 1000,
    duration: Math.round(randRange(rng, BEER.minDuration, BEER.maxDuration) * 1000) / 1000,
  };
}

/**
 * Takes a sip if the cooldown allows one. Returns the roll, or null if E was
 * pressed too soon after the last pull - the caller uses that to decide whether
 * anything audible happened.
 *
 * A sip taken while an earlier lurch is still swelling replaces it rather than
 * adding to it. Two overlapping shoves would be a way to *cancel* the cost by
 * drinking again, and the cost is the point.
 */
export function takeSip(state: BeerState, step: number): SipRoll | null {
  if (state.cooldown > 0) return null;

  const roll = rollSip(step, state.sips);
  state.sips++;
  state.cooldown = BEER.cooldown;
  state.direction.x = roll.direction.x;
  state.direction.y = roll.direction.y;
  state.strength = roll.strength;
  state.duration = roll.duration;
  state.timer = roll.duration;
  state.boost = Math.min(BEER.maxBoost, state.boost + BEER.boostPerSip);
  return roll;
}

/**
 * Smooth 0 -> 1 -> 0 over the life of the lurch. The same envelope the scripted
 * stumbles use, so the two read as the same kind of event to the hands.
 */
function envelope(u: number): number {
  if (u <= 0 || u >= 1) return 0;
  return Math.sin(Math.PI * u);
}

/** The lurch as it stands right now. Call before `advanceBeer`. */
export function sampleBeer(state: BeerState): BeerSample {
  if (state.timer <= 0 || state.duration <= 0) {
    return { wish: vec2(), accel: vec2(), intensity: 0 };
  }
  const elapsed = state.duration - state.timer;
  const env = envelope(elapsed / state.duration) * state.strength;
  return {
    wish: vec2(state.direction.x * env, state.direction.y * env),
    accel: vec2(state.direction.x * env * BEER.impulse, state.direction.y * env * BEER.impulse),
    intensity: env,
  };
}

/**
 * Counts a timer down by one step.
 *
 * The snap to zero is not cosmetic. Subtracting 1/120 from 0.8 ninety-six times
 * leaves a few 1e-16 behind rather than landing on zero, and without this the
 * cooldown would quietly cost one step more than the number in the config says.
 * Both answers are deterministic; this is the one that matches the tuning.
 */
function tickDown(value: number, dt: number): number {
  const next = value - dt;
  return next <= 1e-9 ? 0 : next;
}

/** One fixed step of bookkeeping: the boost bleeds, the lurch runs out. */
export function advanceBeer(state: BeerState, dt: number): void {
  state.boost = tickDown(state.boost, BEER.boostDecayPerSecond * dt);
  state.cooldown = tickDown(state.cooldown, dt);
  if (state.timer > 0) {
    state.timer = tickDown(state.timer, dt);
    if (state.timer === 0) state.strength = 0;
  }
}

/** The boost as 0..1 of its ceiling, for the HUD bar. */
export function beerFraction(state: BeerState): number {
  if (BEER.maxBoost <= 0) return 0;
  return Math.min(1, Math.max(0, state.boost / BEER.maxBoost));
}
